import { useCallback, useEffect, useState } from 'react';
import {
  useParams,
  useLocation,
  useNavigate,
  generatePath,
  useMatch,
  useOutletContext,
} from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import {
  BlacklistDataContent,
  ButtonPseudo,
  OutgoingSidepanel,
  SidePanel,
  SidePanelType,
  DocumentType,
  DocumentStatus,
  PermissionEnum,
  BlacklistUserInfo,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  clearTransactionState,
  setBlacklistDataVisible,
  setBlacklistData,
  changePanelState,
  setPanelIsOpen,
  setBlacklistItemVisible,
  setBlacklistDataItem,
} from '../store/slices/outgoing-transaction';
import {
  useLazyGetTransactionAmlQuery,
  useLazyGetCustomersTransactionLogsQuery,
  useLazyGetCustomerOutgoingTransactionQuery,
  useLazyGetCustomerOutgoingTransactionWarningsQuery,
  useLazyGetProblemTransactionChatsQuery,
  useLazyGetProblemTransactionsDocumentsQuery,
  useLazyGetProblemTransactionDocumentByIdQuery,
  useLazyGetProblemTransactionDocumentFileQuery,
  useDeleteProblemTransactionDocumentsMutation,
  useChangeProblemTransactionDocumentCategoryMutation,
  useChangeProblemTransactionDocumentStatusMutation,
  useAddCommentToProblemTransactionDocumentMutation,
  useDeleteCommentInProblemTransactionDocumentMutation,
  useEditCommentInProblemTransactionDocumentMutation,
} from '../store/services/customers';

const ProblemOutgoingSuspiciousTransactionHOC = ({
  matchPath,
  backPath,
  generalPath,
  amlPath,
  orderPath,
  logPath,
  warningPath,
  chatPath,
  documentPath,
  changeLogPath,
  commentsPath,
}: {
  matchPath: string;
  backPath: string;
  generalPath: string;
  amlPath: string;
  orderPath: string;
  logPath: string;
  warningPath: string;
  chatPath: string;
  documentPath: string;
  changeLogPath: string;
  commentsPath: string;
}): React.JSX.Element => {
  const {
    customersProblemTransactions: { outgoing },
    customersDictionaries: { activePermissions, currenciesList },
  } = useAppSelector((state) => state);

  const [imgLink, setImgLink] = useState('');

  const { customerId, transactionId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const match = useMatch(matchPath);

  const { sendMessage } = useOutletContext<{
    sendMessage: SendMessage | null;
  }>();

  const dispatch = useAppDispatch();

  const [getOutgoingAml, { isLoading: isLoadingAml }] =
    useLazyGetTransactionAmlQuery();

  const [getOutgoingLogs, { isLoading: isLoadingLog }] =
    useLazyGetCustomersTransactionLogsQuery();

  const [getChats] = useLazyGetProblemTransactionChatsQuery();

  const [
    getCustomerOutgoingTransactionWarningsQuery,
    { isLoading: warningsIsLoading },
  ] = useLazyGetCustomerOutgoingTransactionWarningsQuery();

  const [
    getCustomerOutgoingTransaction,
    { isLoading: transactionIsLoading, isFetching },
  ] = useLazyGetCustomerOutgoingTransactionQuery();

  const [getTransactionDocuments] =
    useLazyGetProblemTransactionsDocumentsQuery();
  const [
    getTransactionDocumentyId,
    { isLoading: documentIsLoading, isFetching: documentIsFetching },
  ] = useLazyGetProblemTransactionDocumentByIdQuery();
  const [
    getTransactionDocumentFile,
    {
      isLoading: downloadInProgress,
      isFetching: downloadIsFetching,
      isSuccess: downloadFileIsSuccess,
    },
  ] = useLazyGetProblemTransactionDocumentFileQuery();
  const [
    deleteDocument,
    { isLoading: deleteDocumentInProgress, isSuccess: deleteDocumentIsSuccess },
  ] = useDeleteProblemTransactionDocumentsMutation();
  const [
    changeDocumentCategory,
    { isLoading: documentCategoryChangeInProgress },
  ] = useChangeProblemTransactionDocumentCategoryMutation();
  const [changeDocumentStatus, { isLoading: documentStatusChangeInProgress }] =
    useChangeProblemTransactionDocumentStatusMutation();
  const [addComment, { isLoading: commentInProgress }] =
    useAddCommentToProblemTransactionDocumentMutation();
  const [
    editComment,
    { isLoading: editCommentInProgress, isSuccess: editCommentIsSuccess },
  ] = useEditCommentInProblemTransactionDocumentMutation();
  const [deleteComment, { isLoading: deleteCommentInProgress }] =
    useDeleteCommentInProblemTransactionDocumentMutation();

  const getOutgoingTransactionById = useCallback(() => {
    getCustomerOutgoingTransaction(transactionId as string);
    getOutgoingAml(transactionId as string);
    getCustomerOutgoingTransactionWarningsQuery(transactionId as string);

    if (
      activePermissions?.includes(
        PermissionEnum.TRANSACTION_QUEUE_ITEMS_OUTGOING_LIST,
      )
    ) {
      getOutgoingLogs(transactionId as string);
    }

    if (activePermissions?.includes(PermissionEnum.CHANNELS_LIST)) {
      getChats(transactionId as string);
    }

    if (
      activePermissions?.includes(PermissionEnum.TRANSACTIONS_DOCUMENTS_LIST)
    ) {
      getTransactionDocuments({ id: transactionId as string });
    }
  }, [transactionId, activePermissions]);

  const handleClose = () => {
    dispatch(setPanelIsOpen(false));

    setTimeout(() => {
      navigate(
        generatePath(`${backPath}${search || ''}`, {
          customerId,
        }),
      );
    }, 100);
  };

  const handleCloseBlacklistData = () => {
    dispatch(setBlacklistDataVisible(false));
    dispatch(setBlacklistData(undefined));
    dispatch(setBlacklistItemVisible(false));
    dispatch(setBlacklistDataItem(undefined));
    dispatch(changePanelState({ state: true, transactionId }));
  };

  const getTransactionDocumentyIdAction = async (id: string) => {
    getTransactionDocumentyId({
      id: transactionId as string,
      documentId: id,
    });

    const response = await getTransactionDocumentFile({
      id: transactionId as string,
      documentId: id,
    });

    if (response && response.data) {
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      setImgLink(blob);
    }
  };

  const onDeleteDocument = (id: string) => {
    deleteDocument({
      id: transactionId as string,
      body: {
        ids: [id],
      },
    });
  };

  const onChangeCustomerDocumentsStatus = (data: {
    ids: string[];
    status: DocumentStatus;
  }) => {
    changeDocumentStatus({
      id: transactionId as string,
      body: data,
    });
  };

  const onMoveDocument = (data: { ids: string[]; category: DocumentType }) => {
    changeDocumentCategory({
      id: transactionId as string,
      body: data,
    });
  };
  const onDownloadDocument = async (id: string) => {
    const response = await getTransactionDocumentFile({
      id: transactionId as string,
      documentId: id,
    });

    const currentDocument = outgoing?.documents?.find((item) => item.id === id);

    if (response.data) {
      const hiddenElement = document.createElement('a');

      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${currentDocument?.name}`;
      hiddenElement.click();
    }
  };

  const onSendDocumentComment = (text: string) => {
    addComment({
      id: outgoing?.document?.id as string,
      body: {
        text,
      },
    });
  };

  const onEditComment = (data: {
    id: string;
    commentId: string;
    body: {
      text: string;
    };
  }) => {
    editComment({
      id: data.id,
      commentId: data.commentId,
      body: data.body,
    });
  };

  const onDeleteComment = (data: { id: string; commentId: string }) => {
    deleteComment({
      id: data.id,
      commentId: data.commentId,
    });
  };

  useEffect(() => {
    dispatch(setPanelIsOpen(true));

    return () => {
      dispatch(clearTransactionState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (match && customerId && transactionId) {
      navigate(
        generatePath(`${generalPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
      );
    }
  }, [match, customerId, transactionId]);

  useEffect(() => {
    if (customerId && !outgoing?.transaction && transactionId) {
      getOutgoingTransactionById();
    }
  }, [
    getOutgoingTransactionById,
    customerId,
    outgoing?.transaction,
    transactionId,
  ]);

  return (
    <>
      <OutgoingSidepanel
        panelIsOpen={outgoing.panelIsOpen}
        handleClose={handleClose}
        title="Outgoing Transaction"
        tabs={{
          generalPath: generatePath(`${generalPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          orderPath: generatePath(`${orderPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          amlPath: generatePath(`${amlPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          logPath: generatePath(`${logPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          changeLogPath: generatePath(`${changeLogPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          warningPath: generatePath(`${warningPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          chatTab: generatePath(`${chatPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          documentsPath: generatePath(`${documentPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          commentsPath: generatePath(`${commentsPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
        }}
        isLoading={
          transactionIsLoading ||
          isLoadingAml ||
          isLoadingLog ||
          warningsIsLoading ||
          isFetching
        }
        warnings={outgoing.warnings}
        document={outgoing.document}
        documentFileIsLoading={downloadInProgress || downloadIsFetching}
        downloadInProgress={downloadInProgress || downloadIsFetching}
        imgLink={imgLink}
        onDelete={onDeleteDocument}
        deleteInProgress={deleteDocumentInProgress}
        deleteIsSuccess={deleteDocumentIsSuccess}
        onMove={onMoveDocument}
        moveInProgress={documentCategoryChangeInProgress}
        onDownload={onDownloadDocument}
        sendComment={onSendDocumentComment}
        editComment={onEditComment}
        editCommentInProgress={editCommentInProgress}
        editCommentIsSuccess={editCommentIsSuccess}
        deleteComment={onDeleteComment}
        deleteCommentInProgress={deleteCommentInProgress}
        commentInProgress={commentInProgress}
        statusChangeInProgress={documentStatusChangeInProgress}
        changeCustomerDocumentsStatus={onChangeCustomerDocumentsStatus}
        onDownloadSuccess={downloadFileIsSuccess}
        getTransactionDocument={getTransactionDocumentyIdAction}
        documentIsLoading={documentIsLoading || documentIsFetching}
        activePermission={activePermissions}
        transactionInfo={outgoing?.transaction}
        sendMessage={sendMessage}
        currenciesList={currenciesList}
        sidepanelExpandedKey="problem-outgoing"
      />
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={outgoing.blacklistDataVisible}
        onClose={handleCloseBlacklistData}
        title={outgoing.blacklistData?.fullName as string}
        parent={
          document.getElementById('blacklist-data-sidepanel') as HTMLElement
        }
        footer={
          <ButtonPseudo text="Close" onClick={handleCloseBlacklistData} bold />
        }
        sidepanelExpandedKey="problem-outgoing-blacklist"
      >
        <BlacklistDataContent data={outgoing.blacklistData} />
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={outgoing.blacklistItemVisible}
        onClose={handleCloseBlacklistData}
        title="User Details"
        parent={
          document.getElementById('blacklist-data-sidepanel') as HTMLElement
        }
        footer={
          <ButtonPseudo text="Close" onClick={handleCloseBlacklistData} bold />
        }
        sidepanelExpandedKey="problem-outgoing-blacklist"
        withoutExpand
        expanded
      >
        <BlacklistUserInfo info={outgoing.blacklistItem} isLoading={false} />
      </SidePanel>
      <div id="blacklist-data-sidepanel" />
    </>
  );
};

export default ProblemOutgoingSuspiciousTransactionHOC;
